import {
  Button,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { XIcon } from "lucide-react";
import { useCallback } from "react";
import { RegionDropdown } from "react-country-region-selector";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";

interface StateSelectFormFieldProps {
  register: UseFormRegisterReturn;
  label?: string;
  labelType?: "full" | "short";
  valueType?: "full" | "short";
  name: string;
  country: string;
  dataTestId?: string;
}

export const StateSelectFormField = ({
  register,
  label,
  labelType = "full",
  valueType = "full",
  name,
  country,
  dataTestId,
}: StateSelectFormFieldProps) => {
  const form = useFormContext();

  const customRender = useCallback(
    ({
      options,
    }: {
      options: { label: string; value: string; key: string }[];
    }) => {
      return (
        <FormField
          control={form.control}
          {...register}
          // @ts-expect-error This line is to remove a console error
          ref={null}
          render={({ field }) => (
            <FormItem>
              <div className="flex">
                <FormLabel>{label ?? "State"}</FormLabel>
                <FormMessage
                  data-testid="error-message"
                  className="grow text-right"
                />
              </div>
              <Select
                data-testid={dataTestId}
                disabled={!country}
                name={field.name}
                value={field.value ?? ""}
                onValueChange={(value) => {
                  form.setValue(name, value, {
                    shouldDirty: true,
                  });
                }}
              >
                <FormControl>
                  <div className="relative flex items-center gap-x-2">
                    <SelectTrigger
                      className={cn(!field.value && "text-muted-foreground")}
                    >
                      <SelectValue
                        onBlur={field.onBlur}
                        ref={field.ref}
                        placeholder="Select a state"
                      />
                    </SelectTrigger>
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      className={
                        field.value
                          ? "absolute top-3 right-8 h-4 w-4"
                          : "hidden"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        form.setValue(name, "", {
                          shouldDirty: true,
                        });
                      }}
                    >
                      <XIcon />
                    </Button>
                  </div>
                </FormControl>
                <SelectContent>
                  {/* If we have no options */}
                  {!options.filter((option) => option.value !== "").length && (
                    <SelectItem value="empty" disabled>
                      Select a country first
                    </SelectItem>
                  )}
                  {options
                    .filter((option) => option.value !== "")
                    .map(({ label, value, key }) => (
                      <SelectItem value={value} key={key}>
                        {label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
      );
    },
    [name, country, label, form, register, dataTestId],
  );

  return (
    <RegionDropdown
      value=""
      disabled={!country}
      country={country}
      customRender={customRender}
      labelType={labelType}
      valueType={valueType}
    />
  );
};
